import React from 'react'
import { ContextNotification, Modal } from '@pandiafi/components'
import { Button, SingleDateInput } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import type {
  CoreParkingArea,
  CoreParkingSpot,
} from '../../../common/validations/contractParkingSpotsSchema'
import { todayISO } from './parkingSpots.helpers'
import { safeISODateFromLocaleDate, renderDate } from '../../utils'
import { areaAddressLabel, spotEquipmentLabel, spotTitle } from './uiSelectors'
import { ParkingSpotPrice } from './ParkingSpotPrice'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useAPICreateContractParkingSpotReservation } from 'api/apiHooks/useAPICreateContractParkingSpotReservation'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { ReservationTermsAcceptance } from 'components/ReservationTermsAcceptance/ReservationTermsAcceptance'
import { ReservationRulesAndTermsLink } from 'components/ReservationRulesAndTermsLinks/ReservationRulesAndTermsLinks'

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

const getErrorStatus = (err: unknown): number | undefined => {
  if (!isRecord(err)) return undefined
  const ed = isRecord(err.errorDetails) ? err.errorDetails : undefined
  if (ed && typeof ed.status === 'number') return ed.status
  if (ed && typeof ed.statusCode === 'number') return ed.statusCode
  if (typeof (err as { statusCode?: unknown }).statusCode === 'number')
    return (err as { statusCode: number }).statusCode
  const resp = isRecord((err as { response?: unknown }).response)
    ? (err as { response: { status?: unknown } }).response
    : undefined
  if (resp && typeof resp.status === 'number') return resp.status
  return undefined
}

const getErrorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message
  if (isRecord(err) && typeof err.message === 'string') return err.message
  const resp =
    isRecord(err) &&
    isRecord(err.errorDetails) &&
    isRecord(err.errorDetails.response)
      ? (err.errorDetails.response as Record<string, unknown>)
      : undefined
  if (resp && typeof resp.message === 'string') return resp.message as string
  return undefined
}

export const ParkingSpotReservationForm: React.FC<{
  spot: CoreParkingSpot
  area: CoreParkingArea
  reservationPossibleUntil: string | null
  startDate?: string
  isComplimentary?: boolean
  onSuccess: (effectiveFromISO: string, isComplimentary: boolean) => void
  onClose: () => void
}> = ({
  spot,
  area,
  reservationPossibleUntil,
  startDate,
  isComplimentary = false,
  onSuccess,
  onClose,
}) => {
  const moduleConfig = useModuleConfig()

  const { t } = useTranslation('parking')
  const addressLabel = area ? areaAddressLabel(area) : undefined
  const { currentContractId } = useCurrentContract()

  const moduleLinks = moduleConfig.parkingSpots
  const parkingFallbackLinks: ReservationRulesAndTermsLink[] = [
    ...(moduleLinks?.contractDocumentLink
      ? [
          {
            href: moduleLinks.contractDocumentLink,
            label: t('form.parkingSpotContractDocumentLink'),
          },
        ]
      : []),
    ...(moduleLinks?.termsOfUseDocumentLink
      ? [
          {
            href: moduleLinks.termsOfUseDocumentLink,
            label: t('form.parkingSpotTermsOfUseLink'),
          },
        ]
      : []),
  ]

  const { mutate: createReservation, isPending } =
    useAPICreateContractParkingSpotReservation(currentContractId)

  const [effectiveFrom, setEffectiveFrom] = React.useState<string>(
    () => startDate ?? todayISO()
  )
  const [acceptRules, setAcceptRules] = React.useState(false)
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (startDate) setEffectiveFrom(startDate)
  }, [startDate])

  React.useEffect(() => {
    setAcceptRules(false)
    setErrorMsg(null)
    if (!startDate) setEffectiveFrom(todayISO())
  }, [spot?.rentableParkingSpotId, startDate])

  const minDate = DateTime.fromISO(
    startDate && startDate > todayISO() ? startDate : todayISO()
  )
    .startOf('day')
    .toJSDate()
  const maxDate = reservationPossibleUntil
    ? DateTime.fromISO(reservationPossibleUntil).endOf('day').toJSDate()
    : undefined

  const canSubmit =
    !!currentContractId &&
    !!spot?.rentableParkingSpotId &&
    !!effectiveFrom &&
    acceptRules &&
    !isPending

  const onSubmit = () => {
    if (!currentContractId) {
      setErrorMsg(t('error.contractMissing'))
      return
    }
    if (!spot?.rentableParkingSpotId || !effectiveFrom) return

    setErrorMsg(null)
    createReservation(
      {
        startDate: effectiveFrom,
        endDate: null,
        rentableParkingSpotId: spot.rentableParkingSpotId,
      },
      {
        onSuccess: () => onSuccess(effectiveFrom, isComplimentary),
        onError: (e: unknown) => {
          const fallback = t('error.reservationFailed')

          const statusCode = getErrorStatus(e)
          if (statusCode === 409) {
            setErrorMsg(t('error.overlap'))
            return
          }

          setErrorMsg(getErrorMessage(e) || fallback)
        },
      }
    )
  }

  return (
    <>
      <Modal.Content>
        {(!currentContractId || errorMsg) && (
          <ContextNotification type="error" className="mb-4">
            {errorMsg || t('error.contractMissing')}
          </ContextNotification>
        )}

        <div className="font-bold text-lg">{spotTitle(spot)}</div>
        <div>
          {t('spot.equipment')}: {spotEquipmentLabel(spot, t)}
        </div>
        <ParkingSpotPrice
          spot={spot}
          atISO={startDate ?? null}
          isComplimentary={isComplimentary}
          className="font-semibold mb-6"
        />

        <div className="mb-6">
          <label htmlFor="effectiveFrom" className="font-medium">
            {t('effectiveFrom')}*
          </label>
          <SingleDateInput
            id="effectiveFrom"
            value={renderDate(effectiveFrom)}
            onChange={(val) => {
              const iso = safeISODateFromLocaleDate(val)
              if (iso) setEffectiveFrom(iso)
            }}
            placeholder={t('date.placeholder')}
            datePickerPosition="bottomLeft"
            disabledBefore={minDate}
            disabledAfter={maxDate}
          />
        </div>

        <ReservationTermsAcceptance
          contractId={currentContractId}
          reservableCategory="parking_spot"
          realPropertyHierarchyId={area.realPropertyHierarchyId}
          fallbackLinks={parkingFallbackLinks}
          accepted={acceptRules}
          onAcceptedChange={setAcceptRules}
        >
          {t('acceptRules', {
            address: addressLabel,
          })}
        </ReservationTermsAcceptance>
      </Modal.Content>
      <Modal.Buttons>
        <Button onClick={onClose} variant="cancel">
          {t('form.actions.cancel')}
        </Button>
        <Button onClick={onSubmit} disabled={!canSubmit} loading={isPending}>
          {t('view.reserve')}
        </Button>
      </Modal.Buttons>
    </>
  )
}
